import { CallToActionButton } from '@/components/shared/CallToActionButton';
import { DotBlockEditor } from '@/components/shared/decodeTextBlock';
import { SvgIcon } from '@/components/shared/SvgIcon';
import safeJsonParse from '@/app/utils/safeJSONParse';
import { cn } from '@/app/utils/cn';

const LAYOUT_STYLES = {
    1: {
        section: '',
        headerWrap: 'text-center max-w-3xl mx-auto mb-12',
        eyebrow:
            'inline-block px-4 py-1.5 rounded-full bg-fuschia-100 text-fuschia-700 text-xs font-bold tracking-[0.12em] uppercase mb-6',
        heading:
            'prose max-w-none prose-headings:m-0 prose-h2:text-3xl prose-h2:font-bold prose-h2:leading-tight lg:prose-h2:text-5xl prose-p:m-0 prose-p:empty:hidden mb-6',
        description:
            'prose max-w-none prose-p:m-0 prose-p:text-lg prose-p:text-custom-muted-foreground prose-p:empty:hidden',
        grid: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl mx-auto mb-10',
        card: 'rounded-2xl border border-slate-200 bg-white p-6 hover:border-fuschia-700/40 transition-colors',
        iconWrap:
            'w-10 h-10 rounded-lg bg-fuschia-100 text-fuschia-700 flex items-center justify-center mb-4',
        iconSize: 20,
        cardHeadline: 'text-base font-semibold mb-2',
        cardDescription: 'text-sm text-custom-muted-foreground leading-relaxed',
        ctaPosition: 'center'
    },
    2: {
        section: '',
        headerWrap: 'text-center max-w-3xl mx-auto mb-12',
        eyebrow:
            'inline-block px-4 py-1.5 rounded-full bg-fuschia-100 text-fuschia-700 text-xs font-bold tracking-[0.12em] uppercase mb-6',
        heading:
            'prose max-w-none prose-headings:m-0 prose-h2:text-3xl prose-h2:font-bold prose-h2:leading-tight lg:prose-h2:text-5xl prose-p:m-0 prose-p:empty:hidden mb-6',
        description:
            'prose max-w-none prose-p:m-0 prose-p:text-lg prose-p:text-custom-muted-foreground prose-p:empty:hidden',
        grid: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl mx-auto',
        card: 'rounded-2xl border border-slate-200 bg-custom-muted p-6 hover:border-fuschia-700/40 transition-colors',
        iconWrap:
            'w-10 h-10 rounded-lg bg-fuschia-100 text-fuschia-700 flex items-center justify-center mb-4',
        iconSize: 20,
        cardHeadline: 'text-base font-semibold mb-2',
        cardDescription: 'text-sm text-custom-muted-foreground leading-relaxed',
        ctaPosition: 'center'
    },
    3: {
        section: '',
        split: true,
        splitWrap: 'grid gap-12 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:gap-20',
        headerWrap: 'max-w-xl',
        eyebrow: 'text-sm font-semibold uppercase text-fuschia-700',
        heading:
            'prose max-w-none prose-headings:m-0 prose-h2:text-4xl prose-h2:font-semibold prose-h2:leading-tight prose-p:m-0 prose-p:empty:hidden mt-3',
        description:
            'prose max-w-none prose-p:m-0 prose-p:text-base prose-p:leading-7 prose-p:text-custom-muted-foreground prose-p:empty:hidden mt-5',
        grid: 'grid gap-px overflow-hidden rounded-lg border border-slate-200 bg-slate-200 sm:grid-cols-2',
        card: 'bg-white p-7',
        iconWrap:
            'flex size-10 items-center justify-center rounded-md bg-fuschia-100 text-fuschia-700',
        iconSize: 20,
        cardHeadline: 'mt-5 text-base font-semibold',
        cardDescription: 'mt-2 text-sm leading-6 text-custom-muted-foreground',
        ctaPosition: 'left',
        ctaInHeader: true
    },
    // Layout 4 dividers use nth-child math — item count must be a multiple of the column
    // count (4 at lg, 2 at sm). Uneven counts leave dangling right borders and drop
    // bottom borders from the wrong row.
    4: {
        section: '',
        headerWrap: 'max-w-3xl mb-12',
        eyebrow: 'text-sm font-semibold uppercase text-fuschia-700',
        heading:
            'prose max-w-none prose-headings:m-0 prose-h2:text-4xl prose-h2:font-semibold prose-h2:leading-tight prose-p:m-0 prose-p:empty:hidden mt-3',
        description:
            'prose max-w-none prose-p:m-0 prose-p:text-base prose-p:leading-7 prose-p:text-custom-muted-foreground prose-p:empty:hidden max-w-2xl mt-5',
        grid: 'grid sm:grid-cols-2 lg:grid-cols-4',
        card: 'border-b border-slate-200 py-8 last:border-b-0 sm:pr-8 sm:odd:border-r sm:even:pl-8 sm:[&:nth-last-child(-n+2)]:border-b-0 lg:border-r lg:px-8 lg:[&:nth-child(4n+1)]:pl-0 lg:[&:nth-child(4n)]:border-r-0 lg:[&:nth-child(4n)]:pr-0 lg:[&:nth-last-child(-n+4)]:border-b-0',
        iconWrap:
            'flex size-11 items-center justify-center rounded-md bg-fuschia-100 text-fuschia-700 mb-6',
        iconSize: 24,
        cardHeadline: 'text-xl font-semibold',
        cardDescription: 'mt-3 text-sm leading-6 text-custom-muted-foreground',
        ctaPosition: 'left',
        ctaInHeader: true
    },
    // Layout 5 — same divider style as Layout 4 but 3 columns on lg and no
    // section background. Item count must be a multiple of the column count
    // (3 at lg, 2 at sm).
    5: {
        section: '',
        headerWrap: 'max-w-3xl mb-12',
        eyebrow: 'text-sm font-semibold uppercase text-fuschia-700',
        heading:
            'prose max-w-none prose-headings:m-0 prose-h2:text-4xl prose-h2:font-semibold prose-h2:leading-tight prose-p:m-0 prose-p:empty:hidden mt-3',
        description:
            'prose max-w-none prose-p:m-0 prose-p:text-base prose-p:leading-7 prose-p:text-custom-muted-foreground prose-p:empty:hidden max-w-2xl mt-5',
        grid: 'grid sm:grid-cols-2 lg:grid-cols-3',
        card: 'border-b border-slate-200 py-8 last:border-b-0 sm:pr-8 sm:odd:border-r sm:even:pl-8 sm:[&:nth-last-child(-n+2)]:border-b-0 lg:border-r lg:px-8 lg:[&:nth-child(3n+1)]:pl-0 lg:[&:nth-child(3n)]:border-r-0 lg:[&:nth-child(3n)]:pr-0 lg:[&:nth-last-child(-n+3)]:border-b-0',
        iconWrap:
            'flex size-11 items-center justify-center rounded-md bg-fuschia-100 text-fuschia-700 mb-6',
        iconSize: 24,
        cardHeadline: 'text-xl font-semibold',
        cardDescription: 'mt-3 text-sm leading-6 text-custom-muted-foreground',
        ctaPosition: 'left',
        ctaInHeader: true
    }
};

function normalizeLayout(layout) {
    const n = Number(layout);
    return LAYOUT_STYLES[n] ? n : 1;
}

// Block-editor fields arrive as { json: <doc> } from GraphQL. Accept a raw doc,
// a stringified doc, or the wrapped shape.
function extractBlocks(field) {
    if (!field) return null;
    if (typeof field === 'string') return safeJsonParse(field);
    if (field.json !== undefined) {
        return typeof field.json === 'string' ? safeJsonParse(field.json) : field.json;
    }
    return field;
}

function Item({ icon, title, description, callToAction, style }) {
    const [button] = Array.isArray(callToAction) ? callToAction : [];
    const showCTA = !!(button?.url && button?.urlName);
    return (
        <article className={style.card}>
            {icon && (
                <div className={style.iconWrap}>
                    <SvgIcon icon={icon} size={style.iconSize} />
                </div>
            )}
            {title && <h3 className={style.cardHeadline}>{title}</h3>}
            {description && <p className={style.cardDescription}>{description}</p>}
            {showCTA && (
                <div className="mt-4">
                    <CallToActionButton
                        {...button}
                        buttonType={button.buttonType || 'link'}
                        position="left"
                        noPadding={false}
                        className="!px-0">
                        {button.urlName}
                    </CallToActionButton>
                </div>
            )}
        </article>
    );
}

function Header({ eyebrow, headingBlocks, descriptionBlocks, style, cta }) {
    if (!eyebrow && !headingBlocks && !descriptionBlocks && !cta) return null;
    return (
        <div className={style.headerWrap}>
            {eyebrow && <span className={style.eyebrow}>{eyebrow}</span>}
            {headingBlocks && (
                <div className={style.heading}>
                    <DotBlockEditor blocks={headingBlocks} />
                </div>
            )}
            {descriptionBlocks && (
                <div className={style.description}>
                    <DotBlockEditor blocks={descriptionBlocks} />
                </div>
            )}
            {cta && <div className="mt-8">{cta}</div>}
        </div>
    );
}

function Grid({ items, style }) {
    if (!items.length) return null;
    return (
        <div className={style.grid}>
            {items.map((item, i) => (
                <Item
                    key={item?.identifier ?? item?.inode ?? i}
                    icon={item?.icon}
                    title={item?.title || item?.headline}
                    description={item?.description || item?.body}
                    callToAction={item?.callToAction}
                    style={style}
                />
            ))}
        </div>
    );
}

function GovernanceGrid({
    governanceLayout: layout,
    eyebrow,
    governanceHeading: heading,
    governanceDescription: description,
    items,
    callToAction
}) {
    const style = LAYOUT_STYLES[normalizeLayout(layout)];
    const list = Array.isArray(items) ? items : [];
    const [button] = Array.isArray(callToAction) ? callToAction : [];
    const showCTA = !!(button?.url && button?.urlName);
    const headingBlocks = extractBlocks(heading);
    const descriptionBlocks = extractBlocks(description);

    // !px-6 beats the `.link-no-px a { @apply px-0 }` global rule when a CMS
    // row above sets `link-no-px` on the container.
    const ctaNode = showCTA ? (
        <CallToActionButton
            {...button}
            position={style.ctaPosition}
            noPadding={false}
            className="!px-6">
            {button.urlName}
        </CallToActionButton>
    ) : null;

    const headerNode = (
        <Header
            eyebrow={eyebrow}
            headingBlocks={headingBlocks}
            descriptionBlocks={descriptionBlocks}
            style={style}
            cta={style.ctaInHeader ? ctaNode : null}
        />
    );
    const gridNode = <Grid items={list} style={style} />;

    return (
        <section
            data-component="governance-grid"
            data-layout={normalizeLayout(layout)}
            className={cn('py-20 lg:py-24', style.section)}>
            <div className="container mx-auto px-4">
                {style.split ? (
                    <div className={style.splitWrap}>
                        {headerNode}
                        {gridNode}
                    </div>
                ) : (
                    <>
                        {headerNode}
                        {gridNode}
                    </>
                )}

                {ctaNode && !style.ctaInHeader && <div className="mt-10">{ctaNode}</div>}
            </div>
        </section>
    );
}

export default GovernanceGrid;
